import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  BookIcon,
  ChangeAccount,
  ConfigurationIcon,
  LogoutIcon,
  RayIcon,
  SupportIcon,
  VideoIcon,
} from '@/lib/icon';
import { DownArrowIcon } from '@/lib/icon/DownArrowIcon';
import { Divide, Flex } from '@/lib/v2/components/Layout/Stack';

import { useOnClickOutside } from '@/src/application/hooks/useOnClickOutside';
import { useUserData } from '@/src/application/hooks/useUserData';
import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { sendEvent } from '@/src/compat/iframeIntercom';
import configData from '@/src/config.json';

export type UserProfileProps = {
  name?: string;
  email?: string;
  accountId?: string;
  companyName?: string;
  dropDown?: boolean;
};

export const CompanyData = ({ accountId, companyName }: UserProfileProps) => {
  const { t } = useTranslation();

  const onChangeAccount = useCallback(() => {
    window.location.href = configData.TOP_BAR.CHANGE_ACCOUNT;
  }, []);

  return (
    <div className="w-full px-4 py-2">
      <Flex column itemAlignment="start">
        <Flex>
          <div className="font-medium text-black">{companyName}</div>
          <button onClick={onChangeAccount}>
            <ChangeAccount />
          </button>
        </Flex>
        <div className="mt-2 text-gray-400">
          {t('USER_PROFILE.ID')}: {accountId}
        </div>
      </Flex>
    </div>
  );
};

export const UserProfile = ({
  name,
  email,
  dropDown,
  accountId,
  companyName,
}: UserProfileProps) => {
  const navigate = useNavigate();
  const [userData] = useUserData();
  const [showDropDown, setShowDropDown] = useState(false);
  const [crashRender, setCrashRender] = useState(false);
  const toggleRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const showCrashButton = useFeatureFlag('showCrashButton');
  const isLangEs = userData.language === 'es';
  useOnClickOutside(toggleRef, () => setShowDropDown(false));
  const { t } = useTranslation();

  if (crashRender) {
    throw new Error('I crashed the render like a newbie');
  }

  const configOptions = useMemo(
    () =>
      [
        {
          icon: <ConfigurationIcon />,
          title: t('USER_PROFILE.configuration'),
          onAction: () => {
            setShowDropDown(false);
            navigate('/v2/configuration');
          },
        },
        {
          icon: <SupportIcon />,
          title: t('USER_PROFILE.support'),
          onAction: () => {
            setShowDropDown(false);
            window.open(configData.TOP_BAR.SUPPORT.url, '_blank');
          },
        },
        {
          icon: <VideoIcon />,
          title: t('USER_PROFILE.video_help'),
          onAction: () => {
            setShowDropDown(false);
            window.open(configData.TOP_BAR.YOUTUBE.url, '_blank');
          },
        },
        {
          icon: <BookIcon />,
          title: t('USER_PROFILE.help_manual'),
          onAction: () => {
            setShowDropDown(false);
            window.open(configData.TOP_BAR.MANUAL.url, '_blank');
          },
        },
        isLangEs
          ? {
              icon: <BookIcon />,
              title: t('USER_PROFILE.emblue_mail_tips'),
              onAction: () => {
                setShowDropDown(false);
                window.open(configData.TOP_BAR.TIPS.url, '_blank');
              },
            }
          : null,
        showCrashButton
          ? {
              icon: <RayIcon />,
              title: t('USER_PROFILE.show_me_how_you_crash_baby'),
              onAction: () => {
                throw new Error('iCrashed');
              },
            }
          : null,
        showCrashButton
          ? {
              icon: <RayIcon />,
              title: t('USER_PROFILE.show_me_how_you_crash_when_rendering'),
              onAction: () => {
                setCrashRender(true);
              },
            }
          : null,
        {
          icon: <LogoutIcon />,
          title: t('USER_PROFILE.sign_off'),
          onAction: () => {
            setShowDropDown(false);
            sendEvent(configData.HOME.SIGN_OFF, {});
          },
        },
      ].filter((x: unknown) => x),
    [isLangEs, navigate, showCrashButton, t]
  );

  return (
    <div ref={toggleRef}>
      <button
        className="w-full min-w-[300px] rounded-2xl px-4 py-2 text-white shadow-sm"
        type="button"
        onClick={() => setShowDropDown(!showDropDown)}
      >
        <Flex>
          <div className="px-4">
            <Flex column itemAlignment="start">
              <p className="text-[14px] font-medium">{name}</p>
              <p className="text-[12px]">{email}</p>
            </Flex>
          </div>
          <DownArrowIcon color="white" />
        </Flex>
      </button>
      {showDropDown && dropDown && (
        <div
          className="configuration absolute right-[31px] z-20 mt-4 min-w-[300px] bg-white shadow-md"
          style={{ border: '1px solid #D3DDEA' }}
        >
          <div className="py-1" role="none">
            <Divide>
              <Flex column itemAlignment="start">
                <CompanyData accountId={accountId} companyName={companyName} />
                {configOptions.map((element, i) => {
                  return (
                    <a
                      key={i}
                      className="text-sm group flex w-full items-center px-4 py-2 text-gray-700 hover:bg-[#CCDEF8]"
                      href="#"
                      id="menu-item-0"
                      role="menuitem"
                      onClick={element!.onAction}
                    >
                      {element!.icon}
                      <div className="ml-4">{element!.title}</div>
                    </a>
                  );
                })}
              </Flex>
            </Divide>
          </div>
        </div>
      )}
    </div>
  );
};
